import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, ChevronRight } from 'lucide-react';
import PropertyCard from '@/components/PropertyCard';
import { Button } from '@/components/ui/button';
import { getSubArea } from '@/lib/api';

const SubAreaDetailPage = () => {
  const { regionSlug, subSlug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const res = await getSubArea(regionSlug, subSlug);
        setData(res.data);
        document.title = `Villas for Sale in ${res.data.location}, Bali | Lalita Properties`;
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
          meta.setAttribute(
            'content',
            res.data.description ||
              `Browse villas, land, and rentals in ${res.data.location}, ${res.data.region_name}, Bali. ${res.data.total} live listings on Lalita Properties.`
          );
        }
        // JSON-LD Place structured data + BreadcrumbList for nested SEO
        const existing = document.getElementById('subarea-jsonld');
        if (existing) existing.remove();
        const script = document.createElement('script');
        script.id = 'subarea-jsonld';
        script.type = 'application/ld+json';
        script.text = JSON.stringify([
          {
            '@context': 'https://schema.org',
            '@type': 'Place',
            name: `${res.data.location}, Bali`,
            description: res.data.description,
            containedInPlace: {
              '@type': 'Place',
              name: `${res.data.region_name}, Bali`,
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Regions', item: `${window.location.origin}/regions` },
              { '@type': 'ListItem', position: 2, name: res.data.region_name, item: `${window.location.origin}/region/${regionSlug}` },
              { '@type': 'ListItem', position: 3, name: res.data.location },
            ],
          },
        ]);
        document.head.appendChild(script);
      } catch (e) {
        if (e.response?.status === 404) setNotFound(true);
        if (process.env.NODE_ENV !== 'production') console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [regionSlug, subSlug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-warm-sand flex items-center justify-center pt-20">
        <div className="text-center px-4">
          <h1 className="font-heading text-3xl text-charcoal mb-3">Sub-area not found</h1>
          <Button onClick={() => navigate(`/region/${regionSlug}`)} data-testid="back-to-region">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to {regionSlug}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-warm-sand min-h-screen" data-testid="sub-area-page">
      {/* Hero with breadcrumb */}
      <section className="bg-ubud-jungle text-white pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb trail — also visible for screen readers + SEO */}
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-white/60 mb-6">
            <Link to="/regions" className="hover:text-temple-gold transition-colors" data-testid="bc-regions">Regions</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={`/region/${regionSlug}`} className="hover:text-temple-gold transition-colors" data-testid="bc-region">
              {data?.region_name || regionSlug}
            </Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/90">{data?.location || subSlug}</span>
          </nav>

          {loading || !data ? (
            <div className="space-y-4">
              <div className="h-12 w-64 bg-white/10 rounded animate-pulse" />
              <div className="h-4 w-96 bg-white/10 rounded animate-pulse" />
            </div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-4 h-4 text-temple-gold" />
                <span className="text-xs text-temple-gold uppercase tracking-[0.25em]">
                  {data.region_name}, Bali
                </span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light mb-2 leading-tight">
                {data.location}
              </h1>
              {data.tagline && (
                <p className="font-body italic text-white/80 text-lg mb-5">{data.tagline}</p>
              )}
              {data.description && (
                <p className="font-body text-white/75 max-w-3xl leading-relaxed">{data.description}</p>
              )}
              <div className="flex flex-wrap items-center gap-6 mt-6 text-sm">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-temple-gold" />
                  {data.for_sale} for sale
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-white/40" />
                  {data.for_rent_long} long-term rentals
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-300" />
                  {data.for_rent_short} short stays
                </span>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Property grid */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl h-80 animate-pulse" />
              ))}
            </div>
          ) : data && data.properties.length > 0 ? (
            <>
              <div className="flex items-baseline justify-between mb-6">
                <h2 className="font-heading text-2xl text-charcoal">
                  Properties in {data.location}
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {data.properties.map((p) => (
                  <PropertyCard key={p.id} property={p} />
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl">
              <p className="font-heading text-2xl text-charcoal mb-2">
                Nothing live in {data?.location} yet
              </p>
              <p className="text-charcoal-muted mb-6 max-w-md mx-auto">
                We&apos;re actively sourcing here. Tell us what you&apos;re looking for and we&apos;ll reach out as soon as inventory arrives.
              </p>
              <Button asChild>
                <Link to="/contact">Get in touch</Link>
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default SubAreaDetailPage;
